/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import i18n from 'i18next';
import { GeoJSON } from 'ol/format';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { InitialMapProps } from '@opengeoweb/core';
import { ConfigurableMapConnect } from '@opengeoweb/core';
import { dateUtils, usePrevious } from '@opengeoweb/shared';
import type { CoreAppStore } from '@opengeoweb/store';
import { mapSelectors } from '@opengeoweb/store';
import { webmapUtils } from '@opengeoweb/webmap';
import type { ClickOnFeature, FeatureEvent } from '@opengeoweb/webmap-react';
import {
  genericOpenLayersFeatureStyle,
  MapWarningProperties,
  OpenLayersFeatureLayer,
} from '@opengeoweb/webmap-react';
import { addColors } from '../../utils/addColors';
import { useAllFeedAlerts } from '../../utils/cap-api/hooks';
import { findEventIndexInLanguage } from '../../utils/findEventIndexInLanguage';
import { highlightGeoJson } from '../../utils/highlightGeoJson';
import type { CapPresets, CapFeatures, CustomFeature } from '../types';

export interface MapViewCapConnectProps {
  id?: string;
  title?: string;
  bbox?: { left: number; bottom: number; right: number; top: number };
  srs?: string;
  capWarningPresets: CapPresets;
  layers?: InitialMapProps['mapPreset']['layers'];
  children?: React.ReactNode;
}

export const MapViewCapConnect: React.FC<MapViewCapConnectProps> = ({
  id,
  title,
  bbox,
  srs,
  capWarningPresets,
  layers,
  children,
}) => {
  const { results: feedAlertsResult } = useAllFeedAlerts(capWarningPresets);

  const [featuresByAlert, setFeaturesByAlert] = React.useState<
    Record<string, CustomFeature[]>
  >({});

  const [selectedFeatureEvent, setSelectedFeatureEvent] = React.useState<
    FeatureEvent | undefined
  >(undefined);

  const mapIdRef = React.useRef(id || webmapUtils.generateMapId());

  const parentSizeRef = React.useRef<HTMLDivElement>(null);
  const [parentSize, setParentSize] = React.useState(window.innerWidth / 2);

  React.useEffect(() => {
    const handleWindowResize = (): void => {
      setParentSize(parentSizeRef.current!.parentElement!.offsetWidth);
    };
    window.addEventListener('resize', handleWindowResize);
    return (): void => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  const selectedTime = useSelector((store: CoreAppStore) =>
    mapSelectors.getSelectedTime(store, mapIdRef.current),
  );

  const selectedDate = useMemo(
    () => dateUtils.createDate(dateUtils.fromUnix(selectedTime).toISOString()),
    [selectedTime],
  );

  const alertsInEffect = useMemo(
    () =>
      feedAlertsResult.filter((alert) =>
        dateUtils.isBetween(
          selectedDate,
          dateUtils.createDate(alert.alert.onset),
          dateUtils.createDate(alert.alert.expires),
          '[)',
        ),
      ),
    [feedAlertsResult, selectedDate],
  );

  const alertIdsInEffect = useMemo(
    () => alertsInEffect.map(({ alert }) => alert.identifier),
    [alertsInEffect],
  );

  const previousAlertIdsInEffect = usePrevious(alertIdsInEffect);

  const clickOnFeature: ClickOnFeature = useMemo(() => {
    return {
      onClick: (feature): void => {
        const featureResults: FeatureEvent | undefined =
          feature.length === 0
            ? undefined
            : {
                feature: new GeoJSON().writeFeatureObject(feature[0]),
                coordinateIndexInFeature: null!,
                featureIndex: null!,
                screenCoords: null!,
                mouseX: null!,
                mouseY: null!,
                isInEditMode: null!,
              };

        setSelectedFeatureEvent(featureResults);
        setFeaturesByAlert((previous) => {
          return highlightGeoJson(previous, featureResults);
        });
      },
    };
  }, []);

  React.useEffect(() => {
    if (
      JSON.stringify(alertIdsInEffect) !==
      JSON.stringify(previousAlertIdsInEffect)
    ) {
      const featuresByAlert = alertsInEffect.reduce<
        Record<string, CustomFeature[]>
      >(
        (byId, { alert, features }) => ({
          ...byId,
          [alert.identifier]: addColors(features),
        }),
        {},
      );
      setFeaturesByAlert(featuresByAlert);
    }
  }, [alertIdsInEffect, alertsInEffect, previousAlertIdsInEffect]);

  const geoJsonObject = useMemo((): CapFeatures => {
    const featuresInEffect = Object.keys(featuresByAlert)
      .filter((id) => alertIdsInEffect.includes(id))
      .flatMap((id) => featuresByAlert[id]);

    return {
      type: 'FeatureCollection',
      features: featuresInEffect,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featuresByAlert]);

  return (
    <div
      data-testid="mapViewCap"
      ref={parentSizeRef}
      style={{ height: '100%', width: '100%' }}
    >
      <ConfigurableMapConnect
        data-testid="capWarningMap"
        id={mapIdRef.current}
        initialBbox={bbox}
        layers={layers ?? []}
        srs={srs}
        title={title}
      >
        <OpenLayersFeatureLayer
          clickOnFeature={clickOnFeature}
          featureCollection={geoJsonObject}
          style={genericOpenLayersFeatureStyle}
          testId="MapViewCap-features"
          zIndex={1000}
        />

        {selectedFeatureEvent ? (
          <MapWarningProperties
            data-testid="mapWarnings"
            languageIndex={findEventIndexInLanguage(
              i18n.language,
              selectedFeatureEvent.feature.properties!.details.languages,
              capWarningPresets!,
              selectedFeatureEvent.feature.properties!.details.feedAddress,
            )}
            parentSize={parentSize}
            selectedFeatureProperties={
              selectedFeatureEvent.feature.properties!.details
            }
          />
        ) : null}
        {children}
      </ConfigurableMapConnect>
    </div>
  );
};
